import OpenAI from "openai";
import { QuestionType } from "./types";

const formatAnswerPrompt = `
Format your answer as the following JSON object, replacing the content within the <>:

{
  "response": <RESPONSE>
  "attacking_candidate": <Candidate Last Name or NOONE>
  "should_moderator_follow_up": <true or false>
}
`;

export const getCandidatePrompt = (candidateName: string, oppositionName: string, question: string) => {
    return `
You are acting like ${candidateName} at 
the 2024 Presidential Debate. The moderator will ask you a 
question enclosed in <question> tags and you are to respond in a way that ${candidateName} 
would. You are debating against ${oppositionName}. 
You can choose to direct parts of your answer to ${oppositionName}, 
or not mention ${oppositionName} at all.
${formatAnswerPrompt}
Moderator: <question>${question}</question>`
}

export const getCandidateRebuttalPrompt = (candidateName: string, oppositionName: string, question: string, opp_response: string) => {
    return `
You are acting like ${candidateName} at 
the 2024 Presidential Debate. ${oppositionName} 
has just been asked a question by the moderator. ${oppositionName} 
mentioned you in their response, so you have the opportunity 
to rebuttal. You can attack ${oppositionName} based on what they said 
about you, but try to tie the response back to the question. Respond 
in a debate/conversational tone in the way ${candidateName} would.

Moderator: ${question}
${oppositionName}: ${opp_response}
${formatAnswerPrompt}`
}

export const getQuestionPrompt = (candidateName: string, question: string, type: QuestionType) => {
    return `
You are a moderator for the 2024 Presidential Debate. Given a ${type === QuestionType.Question ? "broad question" : "topic"} and 
candidate, create a question to ask the candidate for the debate. 
The question should do the following:

1. Be related to the ${type === QuestionType.Question ? "starter question" : "given topic"}
2. Consider the candidate and their previous actions, inactions, policies, and opinions on the topic
3. Consider any current events that may be relevant to the topic (if applicable)

Format your answer as a single paragraph that only includes the context and question, without any filler content before.

Topic: ${question}
Candidate: ${candidateName}`
}

export const getCandidateAttackPrompt = (candidateName: string, oppositionName: string, response: string, rebuttal: string) => {
    return `
You are acting like ${candidateName} at the 2024 Presidential Debate. 
The moderator has just asked you a question, to which you gave an answer. 
You are debating against ${oppositionName}, who made a rebuttal to your answer. 
You are now given 30 seconds to give a response to ${oppositionName}'s rebuttal 
in which you can attack the candidate, the rebuttal, or related policies 
and opinions of the candidate. You do not have to address ${oppositionName} politely.

Your original answer will be enclosed in <answer> tags. 
${oppositionName}'s rebuttal will be enclosed in <rebuttal> tags.

Do not provide any tags in the output. Limit your response to a maximum of 3 sentences.

<answer>${response}</answer>

<rebuttal>${rebuttal}</rebuttal>`
}

export const getCandidateConversationalAttackPrompt = (candidateName: string, oppositionName: string, candidateResponse: string, oppositionResponse: string) => {
    return `
You are acting like ${candidateName} at the 2024 Presidential Debate. 
The moderator has just asked you a question, to which you gave an answer. 
You are debating against ${oppositionName}, who made a rebuttal to your answer. 
You both are now in a fiery back and forth and you have 30 seconds to give a response 
to ${oppositionName}'s response. You do not have to address ${oppositionName} politely.

Your last response will be enclosed in <previous_response> tags. 
${oppositionName}'s response will be enclosed in <opposing_response> tags.

Do not provide any tags in the output. Limit your response to a maximum of 3 sentences.

<previous_response>${candidateResponse}</previous_response>

<opposing_response>${oppositionResponse}</opposing_response>`
}

export const getStreamAiResponse = async (prompt: string) => {
    const openai = new OpenAI({apiKey: process.env.REACT_APP_OPENAI_API_KEY, dangerouslyAllowBrowser: true});

    const stream = await openai.chat.completions.create({
        messages: [{ role: "user", content: prompt }],
        model: "gpt-4-turbo",
        temperature: 0.35,
        stream: true,
    });

    return stream;
}